// Slice 53 — Customer 360 (full-page replacement for CustomerDetailModal).
//
// Eight tabs: Overview, Licenses, Feedback, Instances, Activity, Privacy,
// Billing (placeholder), Danger Zone. Each tab pulls its own slice of data
// (lazy on activation) so the page stays responsive even with hundreds of
// feedback threads / audit rows on a power-user customer.
//
// All cross-cutting privacy + erase flows live in this file because they're
// only opened from here. PRE-GA: the old modal is gone, no compat shim.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Api,
  CONSENT_PURPOSES,
  CONSENT_SOURCES,
  type ConsentPurpose,
  type ConsentSource,
  type CustomerAuditRow,
  type CustomerDetail,
  type CustomerFeedbackThread,
  type CustomerInstanceRow,
  type CustomerTagRow,
  CustomersApi,
  LAWFUL_BASES,
  type LawfulBasis,
} from "../../api/client";
import { useRouter } from "../../router/router";
import { type ToastFn, useShell } from "../../shell/HqShellContext";
import {
  HqAvatar,
  HqBadge,
  HqButton,
  HqCard,
  HqIconButton,
  HqInput,
  HqModal,
  HqRadio,
  HqStatusDot,
  Icons,
} from "../../ui";
import { MonoId } from "../licenses/MonoId";

const BASIS_LABEL: Record<LawfulBasis, string> = {
  contract: "Contract · Art. 6(1)(b)",
  consent: "Consent · Art. 6(1)(a)",
  legitimate_interest: "Legitimate interest · Art. 6(1)(f)",
};
const PURPOSE_LABEL: Record<string, string> = {
  telemetry: "Telemetry",
  marketing: "Marketing",
  product_news: "Product news",
};

type TabId =
  | "overview"
  | "licenses"
  | "feedback"
  | "instances"
  | "activity"
  | "privacy"
  | "billing"
  | "danger";

const TABS: ReadonlyArray<{ id: TabId; label: string; icon: keyof typeof Icons }> = [
  { id: "overview", label: "Overview", icon: "Sparkles" },
  { id: "licenses", label: "Licenses", icon: "Key" },
  { id: "feedback", label: "Feedback", icon: "Inbox" },
  { id: "instances", label: "Instances", icon: "Activity" },
  { id: "activity", label: "Activity", icon: "Audit" },
  { id: "privacy", label: "Privacy", icon: "Shield" },
  { id: "billing", label: "Billing", icon: "Tag" },
  { id: "danger", label: "Danger Zone", icon: "Revoke" },
];

export interface CustomerDetailScreenProps {
  customerId: string;
}

export function CustomerDetailScreen({ customerId }: CustomerDetailScreenProps) {
  const { navigate } = useRouter();
  const { toast, refreshTick, bumpRefresh } = useShell();
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>("overview");
  const [tags, setTags] = useState<CustomerTagRow[]>([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const [d, t] = await Promise.all([
      CustomersApi.get(customerId),
      CustomersApi.listTags(customerId),
    ]);
    setLoading(false);
    if (!d.ok) {
      setErr(d.error.message);
      setDetail(null);
      return;
    }
    setDetail(d.data);
    if (t.ok) setTags(t.data);
  }, [customerId]);

  useEffect(() => {
    void refreshTick;
    void refresh();
  }, [refresh, refreshTick]);

  if (loading && !detail) return <CenteredMessage tone="dim">Loading customer…</CenteredMessage>;
  if (err) return <CenteredMessage tone="danger">{err}</CenteredMessage>;
  if (!detail) return <CenteredMessage tone="danger">Customer not found.</CenteredMessage>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Crumbs onBack={() => navigate("customers")} />
      <Header detail={detail} tags={tags} />
      <KpiStrip detail={detail} />
      <TagStrip
        customerId={customerId}
        tags={tags}
        tombstoned={detail.tombstonedAt != null}
        onChanged={() => void refresh()}
      />
      <TabBar tab={tab} onChange={setTab} />
      <TabBody
        tab={tab}
        detail={detail}
        onRefresh={() => {
          void refresh();
          bumpRefresh();
        }}
        toast={toast}
      />
    </div>
  );
}

function CenteredMessage({
  tone,
  children,
}: {
  tone: "dim" | "danger";
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: 48,
        textAlign: "center",
        fontSize: 13,
        color: tone === "danger" ? "var(--hq-danger)" : "var(--hq-text-dim)",
      }}
    >
      {children}
    </div>
  );
}

function Crumbs({ onBack }: { onBack: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <HqButton variant="ghost" size="sm" leadingIcon={Icons.ChevD} onClick={onBack}>
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          Back to Customers
        </span>
      </HqButton>
    </div>
  );
}

function Header({ detail, tags }: { detail: CustomerDetail; tags: CustomerTagRow[] }) {
  const tombstoned = detail.tombstonedAt != null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        paddingBottom: 14,
        borderBottom: "1px solid var(--hq-border)",
      }}
    >
      <HqAvatar name={detail.displayName ?? detail.emailMasked} size={56} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-.01em" }}>
            {detail.displayName ?? <em style={{ color: "var(--hq-text-dim)" }}>no name</em>}
          </span>
          {tombstoned && <HqBadge tone="warn">tombstoned (Art. 17)</HqBadge>}
          {detail.isEea && !tombstoned && <HqBadge tone="success">EEA</HqBadge>}
          {tags.map((t) => (
            <HqBadge key={t.tag} tone="accent" size="sm">
              {t.tag}
            </HqBadge>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 6,
            color: "var(--hq-text-muted)",
          }}
        >
          <span className="hq-mono" style={{ fontSize: 12 }}>
            {detail.emailMasked}
          </span>
          <span style={{ color: "var(--hq-text-dim)", fontSize: 11 }}>·</span>
          <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
            {detail.id}
          </span>
        </div>
      </div>
    </div>
  );
}

function KpiStrip({ detail }: { detail: CustomerDetail }) {
  const activeLicenses = detail.linkedLicenses.filter((l) => !l.revoked).length;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
      <Kpi
        label="Licenses"
        value={`${activeLicenses}/${detail.linkedLicenses.length}`}
        hint="active / total"
      />
      <Kpi label="Feedback" value={detail.feedbackCount.toString()} hint="threads" />
      <Kpi label="Country" value={detail.country ?? "—"} mono />
      <Kpi label="Joined" value={fmtDate(detail.createdAt)} mono />
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  mono,
}: {
  label: string;
  value: string;
  hint?: string;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        padding: 14,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 20,
          fontWeight: 600,
          fontFamily: mono ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
        }}
      >
        {value}
      </span>
      {hint && (
        <span className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
          {hint}
        </span>
      )}
    </div>
  );
}

function TagStrip({
  customerId,
  tags,
  tombstoned,
  onChanged,
}: {
  customerId: string;
  tags: CustomerTagRow[];
  tombstoned: boolean;
  onChanged: () => void;
}) {
  const { toast } = useShell();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  async function add() {
    const tag = draft.trim().toLowerCase();
    if (!tag) {
      setAdding(false);
      return;
    }
    setBusy(true);
    const res = await CustomersApi.addTag(customerId, tag);
    setBusy(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setDraft("");
    setAdding(false);
    onChanged();
    if (!res.data.alreadyTagged) toast(`Tagged: ${tag}`, "success");
  }

  async function remove(tag: string) {
    const res = await CustomersApi.removeTag(customerId, tag);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    onChanged();
    toast(`Removed: ${tag}`, "info");
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        Tags
      </span>
      {tags.length === 0 && !adding && (
        <span style={{ fontSize: 12, color: "var(--hq-text-dim)" }}>—</span>
      )}
      {tags.map((t) => (
        <span
          key={t.tag}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 8px",
            background: "var(--hq-bg-input)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: 999,
            fontSize: 11,
            color: "var(--hq-text)",
          }}
        >
          {t.tag}
          {!tombstoned && (
            <button
              type="button"
              onClick={() => void remove(t.tag)}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--hq-text-dim)",
                cursor: "pointer",
                padding: 0,
                fontSize: 12,
                lineHeight: 1,
              }}
              title={`Remove ${t.tag}`}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {adding ? (
        <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              if (!busy) {
                setAdding(false);
                setDraft("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") void add();
              if (e.key === "Escape") {
                setAdding(false);
                setDraft("");
              }
            }}
            ref={(el) => el?.focus()}
            placeholder="tag-name"
            style={{
              background: "var(--hq-bg-input)",
              border: "1px solid var(--hq-border-input)",
              color: "var(--hq-text)",
              borderRadius: 999,
              padding: "3px 8px",
              fontSize: 11,
              outline: "none",
              fontFamily: "var(--hq-font-mono)",
              width: 110,
            }}
          />
        </span>
      ) : !tombstoned ? (
        <HqButton
          variant="ghost"
          size="sm"
          leadingIcon={Icons.Plus}
          onClick={() => setAdding(true)}
        >
          Add tag
        </HqButton>
      ) : null}
    </div>
  );
}

function TabBar({ tab, onChange }: { tab: TabId; onChange: (t: TabId) => void }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 2,
        borderBottom: "1px solid var(--hq-border)",
        marginBottom: -1,
      }}
    >
      {TABS.map((t) => {
        const Icon = Icons[t.icon];
        const active = t.id === tab;
        const danger = t.id === "danger";
        return (
          <button
            type="button"
            key={t.id}
            onClick={() => onChange(t.id)}
            style={{
              padding: "10px 14px",
              background: "transparent",
              border: "none",
              borderBottom: active
                ? `2px solid ${danger ? "var(--hq-danger)" : "var(--hq-accent)"}`
                : "2px solid transparent",
              color: active
                ? danger
                  ? "var(--hq-danger)"
                  : "var(--hq-accent)"
                : "var(--hq-text-muted)",
              cursor: "pointer",
              fontSize: 12.5,
              fontWeight: active ? 600 : 500,
              display: "inline-flex",
              gap: 6,
              alignItems: "center",
            }}
          >
            {Icon && <Icon width={13} height={13} sw={1.7} style={{ color: "currentColor" }} />}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function TabBody({
  tab,
  detail,
  onRefresh,
  toast,
}: {
  tab: TabId;
  detail: CustomerDetail;
  onRefresh: () => void;
  toast: ToastFn;
}) {
  switch (tab) {
    case "overview":
      return <OverviewTab detail={detail} onRefresh={onRefresh} />;
    case "licenses":
      return <LicensesTab detail={detail} onRefresh={onRefresh} />;
    case "feedback":
      return <FeedbackTab detail={detail} />;
    case "instances":
      return <InstancesTab detail={detail} />;
    case "activity":
      return <ActivityTab detail={detail} />;
    case "privacy":
      return <PrivacyTab detail={detail} onRefresh={onRefresh} />;
    case "billing":
      return <BillingTab />;
    case "danger":
      return <DangerTab detail={detail} onRefresh={onRefresh} toast={toast} />;
  }
}

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab({ detail, onRefresh }: { detail: CustomerDetail; onRefresh: () => void }) {
  const { toast } = useShell();
  const [busy, setBusy] = useState<string | null>(null);
  const tombstoned = detail.tombstonedAt != null;

  async function resend() {
    setBusy("resend");
    const res = await CustomersApi.resendLicenseEmail(detail.id);
    setBusy(null);
    if (!res.ok) {
      toast(res.error.message, res.error.code === "email_not_configured" ? "warn" : "danger");
      return;
    }
    toast(`Resent license email (sent=${res.data.sent})`, "success");
    onRefresh();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {detail.notes && (
        <HqCard>
          <div
            className="hq-mono"
            style={{
              fontSize: 9.5,
              color: "var(--hq-text-dim)",
              letterSpacing: ".1em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Internal note
          </div>
          <div
            style={{
              whiteSpace: "pre-wrap",
              color: "var(--hq-text-muted)",
              fontSize: 12.5,
              lineHeight: 1.55,
              fontStyle: "italic",
            }}
          >
            {detail.notes}
          </div>
        </HqCard>
      )}
      <HqCard>
        <div
          className="hq-mono"
          style={{
            fontSize: 9.5,
            color: "var(--hq-text-dim)",
            letterSpacing: ".1em",
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          Quick actions
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <HqButton
            variant="secondary"
            size="sm"
            leadingIcon={Icons.Send ?? Icons.Inbox}
            onClick={() => void resend()}
            disabled={tombstoned || busy === "resend"}
            loading={busy === "resend"}
          >
            Resend license email
          </HqButton>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--hq-text-dim)" }}>
          SMTP transport is not configured yet — resend returns 503 today but the audit row lands
          either way so the intent is on record.
        </div>
      </HqCard>
    </div>
  );
}

// ─── Licenses tab ─────────────────────────────────────────────────────────

function LicensesTab({ detail, onRefresh }: { detail: CustomerDetail; onRefresh: () => void }) {
  const { toast } = useShell();
  const [busy, setBusy] = useState<string | null>(null);

  async function revoke(licenseId: string) {
    const reason = prompt("Revoke reason (lands in audit)?", "operator-initiated");
    if (!reason) return;
    setBusy(licenseId);
    const res = await Api.revokeLicense(licenseId, reason.trim());
    setBusy(null);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    toast("License revoked.", "warn");
    onRefresh();
  }

  async function unrevoke(licenseId: string) {
    setBusy(licenseId);
    const res = await Api.unrevokeLicense(licenseId);
    setBusy(null);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    toast("License restored.", "success");
    onRefresh();
  }

  async function download(licenseId: string) {
    const res = await Api.getLicense(licenseId);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    const json = res.data.signedJson;
    const blob = new Blob([`${json}\n`], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${licenseId}.vyvern-license.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (detail.linkedLicenses.length === 0) {
    return (
      <HqCard>
        <div
          style={{ padding: 24, color: "var(--hq-text-dim)", fontSize: 13, textAlign: "center" }}
        >
          No licenses yet. Issue one from the Licenses screen.
        </div>
      </HqCard>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {detail.linkedLicenses.map((l) => {
        const expiringSoon =
          l.expiresAt != null && l.expiresAt * 1000 - Date.now() < 14 * 86400_000;
        const expired = l.expiresAt != null && l.expiresAt * 1000 < Date.now();
        return (
          <div
            key={l.licenseId}
            style={{
              padding: 12,
              background: "var(--hq-bg-input)",
              border: "1px solid var(--hq-border-input)",
              borderRadius: "var(--hq-radius-md)",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <MonoId value={l.licenseId} short={22} color="var(--hq-text)" />
            <HqBadge
              tone={l.tier === "pro" ? "pro" : l.tier === "beta" ? "beta" : "free"}
              size="sm"
            >
              {l.tier}
            </HqBadge>
            {l.revoked ? (
              <HqBadge tone="danger" size="sm">
                revoked
              </HqBadge>
            ) : expired ? (
              <HqBadge tone="warn" size="sm">
                expired
              </HqBadge>
            ) : expiringSoon ? (
              <HqBadge tone="warn" size="sm">
                expires soon
              </HqBadge>
            ) : (
              <HqBadge tone="success" size="sm">
                active
              </HqBadge>
            )}
            <span style={{ flex: 1 }} />
            <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
              issued {fmtDate(l.createdAt)}
              {l.expiresAt && ` · exp ${fmtDate(l.expiresAt * 1000)}`}
            </span>
            <HqButton
              variant="ghost"
              size="sm"
              leadingIcon={Icons.Download}
              onClick={() => void download(l.licenseId)}
            >
              Download
            </HqButton>
            {l.revoked ? (
              <HqButton
                variant="secondary"
                size="sm"
                onClick={() => void unrevoke(l.licenseId)}
                disabled={busy === l.licenseId}
              >
                Restore
              </HqButton>
            ) : (
              <HqButton
                variant="danger"
                size="sm"
                leadingIcon={Icons.Revoke}
                onClick={() => void revoke(l.licenseId)}
                disabled={busy === l.licenseId}
              >
                Revoke
              </HqButton>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Feedback tab ─────────────────────────────────────────────────────────

function FeedbackTab({ detail }: { detail: CustomerDetail }) {
  const { toast } = useShell();
  const { navigate } = useRouter();
  const [rows, setRows] = useState<CustomerFeedbackThread[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await CustomersApi.feedbackThreads(detail.id);
      if (cancelled) return;
      if (!res.ok) {
        setErr(res.error.message);
        toast(res.error.message, "danger");
        return;
      }
      setRows(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [detail.id, toast]);

  if (err) return <CenteredMessage tone="danger">{err}</CenteredMessage>;
  if (rows == null) return <CenteredMessage tone="dim">Loading feedback…</CenteredMessage>;
  if (rows.length === 0) {
    return (
      <HqCard>
        <div
          style={{ padding: 24, color: "var(--hq-text-dim)", fontSize: 13, textAlign: "center" }}
        >
          No feedback threads from this customer yet.
        </div>
      </HqCard>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((r) => (
        <button
          type="button"
          key={r.id}
          onClick={() => navigate("feedback", r.id)}
          style={{
            textAlign: "left",
            padding: 12,
            background: "var(--hq-bg-input)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-md)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            cursor: "pointer",
            color: "inherit",
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <HqBadge
              tone={
                r.status === "new"
                  ? "accent"
                  : r.status === "closed"
                    ? "neutral"
                    : r.status === "in_progress"
                      ? "warn"
                      : "neutral"
              }
              size="sm"
            >
              {r.status}
            </HqBadge>
            {r.unreadFromUser > 0 && (
              <HqBadge tone="danger" size="sm">
                {r.unreadFromUser} unread
              </HqBadge>
            )}
            <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
              v{r.vyvernVersion}
            </span>
            <span style={{ flex: 1 }} />
            <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
              {fmtDateTime(r.lastMessageAt)}
            </span>
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: "var(--hq-text-muted)",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {r.bodyPreview}
            {r.bodyPreview.length === 140 && "…"}
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Instances tab ────────────────────────────────────────────────────────

function InstancesTab({ detail }: { detail: CustomerDetail }) {
  const [rows, setRows] = useState<CustomerInstanceRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await CustomersApi.instances(detail.id);
      if (cancelled) return;
      if (!res.ok) {
        setErr(res.error.message);
        return;
      }
      setRows(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [detail.id]);

  if (err) return <CenteredMessage tone="danger">{err}</CenteredMessage>;
  if (rows == null) return <CenteredMessage tone="dim">Loading instances…</CenteredMessage>;
  if (rows.length === 0) {
    return (
      <HqCard>
        <div
          style={{ padding: 24, color: "var(--hq-text-dim)", fontSize: 13, textAlign: "center" }}
        >
          No licenses to roll up.
        </div>
      </HqCard>
    );
  }

  return (
    <HqCard padding={false}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr
            style={{
              textAlign: "left",
              background: "var(--hq-bg-raised)",
              borderBottom: "1px solid var(--hq-border)",
            }}
          >
            <Th>License</Th>
            <Th>Tier</Th>
            <Th>Instances</Th>
            <Th>Version</Th>
            <Th>Top feature</Th>
            <Th>Error rate</Th>
            <Th>Last seen</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.licenseId} style={{ borderBottom: "1px solid var(--hq-border)" }}>
              <Td>
                <MonoId value={r.licenseId} short={18} color="var(--hq-text)" />
              </Td>
              <Td>
                <HqBadge
                  tone={r.tier === "pro" ? "pro" : r.tier === "beta" ? "beta" : "free"}
                  size="sm"
                >
                  {r.tier}
                </HqBadge>
              </Td>
              <Td mono>{r.instanceCount}</Td>
              <Td mono>{r.version ?? "—"}</Td>
              <Td mono>{r.topFeature ?? "—"}</Td>
              <Td mono>{r.errorRate == null ? "—" : `${(r.errorRate * 100).toFixed(1)}%`}</Td>
              <Td mono>{r.lastSeenTs ? fmtDateTime(r.lastSeenTs) : "never"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </HqCard>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "10px 14px",
        color: "var(--hq-text-dim)",
        fontFamily: "var(--hq-font-mono)",
        fontSize: 10.5,
        letterSpacing: ".1em",
        textTransform: "uppercase",
        fontWeight: 500,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <td
      style={{
        padding: "10px 14px",
        fontFamily: mono ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
        fontSize: mono ? 11 : 12,
        color: "var(--hq-text)",
      }}
    >
      {children}
    </td>
  );
}

// ─── Activity tab ─────────────────────────────────────────────────────────

function ActivityTab({ detail }: { detail: CustomerDetail }) {
  const [rows, setRows] = useState<CustomerAuditRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await CustomersApi.auditTimeline(detail.id, 200);
      if (cancelled) return;
      if (!res.ok) {
        setErr(res.error.message);
        return;
      }
      setRows(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [detail.id]);

  if (err) return <CenteredMessage tone="danger">{err}</CenteredMessage>;
  if (rows == null) return <CenteredMessage tone="dim">Loading audit timeline…</CenteredMessage>;
  if (rows.length === 0) {
    return (
      <HqCard>
        <div
          style={{ padding: 24, color: "var(--hq-text-dim)", fontSize: 13, textAlign: "center" }}
        >
          No audit rows for this customer yet.
        </div>
      </HqCard>
    );
  }
  return (
    <HqCard padding={false}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {rows.map((r) => (
          <li
            key={r.id}
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--hq-border)",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
              {fmtDateTime(r.at)}
            </span>
            <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-accent)" }}>
              {r.action}
            </span>
            <span style={{ fontSize: 11, color: "var(--hq-text-muted)" }}>by {r.actor}</span>
            {r.targetType === "license" && r.targetId && (
              <MonoId value={r.targetId} short={14} color="var(--hq-text-dim)" />
            )}
            <span style={{ flex: 1 }} />
            <span
              className="hq-mono"
              style={{
                fontSize: 10.5,
                color: "var(--hq-text-dim)",
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={JSON.stringify(r.meta)}
            >
              {summarizeMeta(r.meta)}
            </span>
          </li>
        ))}
      </ul>
    </HqCard>
  );
}

function summarizeMeta(m: Record<string, unknown>): string {
  const keys = Object.keys(m);
  if (keys.length === 0) return "";
  return keys
    .slice(0, 3)
    .map((k) => `${k}=${stringifyValue(m[k])}`)
    .join(" ");
}

function stringifyValue(v: unknown): string {
  if (v == null) return String(v);
  if (typeof v === "string") return v.length > 24 ? `${v.slice(0, 24)}…` : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "…";
}

// ─── Privacy tab ──────────────────────────────────────────────────────────

function PrivacyTab({ detail, onRefresh }: { detail: CustomerDetail; onRefresh: () => void }) {
  const { toast } = useShell();
  const [revealOpen, setRevealOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const tombstoned = detail.tombstonedAt != null;

  async function changeBasis(basis: LawfulBasis) {
    setBusy(true);
    const res = await CustomersApi.update(detail.id, { lawfulBasis: basis });
    setBusy(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    onRefresh();
    toast("Lawful basis updated.", "success");
  }

  async function toggleFeedback() {
    setBusy(true);
    const res = await CustomersApi.setFeedbackEnabled(detail.id, !detail.feedbackEnabled);
    setBusy(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    onRefresh();
    toast(res.data.feedbackEnabled ? "Feedback enabled." : "Feedback disabled.", "success");
  }

  async function downloadExport() {
    const res = await CustomersApi.exportData(detail.id);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    const blob = new Blob([`${JSON.stringify(res.data, null, 2)}\n`], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${detail.id}.gdpr-art15.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onRefresh();
    toast("Art. 15 export ready.", "success");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <HqCard>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <Icons.Shield width={14} height={14} sw={1.7} style={{ color: "var(--hq-accent)" }} />
          <span
            className="hq-mono"
            style={{
              fontSize: 10.5,
              color: "var(--hq-accent)",
              letterSpacing: ".1em",
              textTransform: "uppercase",
            }}
          >
            Consent ledger
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {CONSENT_PURPOSES.map((p) => (
            <ConsentChip key={p} purpose={p} granted={!!detail.currentConsent[p]} />
          ))}
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <HqButton
            variant="ghost"
            size="sm"
            leadingIcon={Icons.Plus}
            onClick={() => setConsentOpen(true)}
            disabled={tombstoned}
          >
            Record consent
          </HqButton>
          <HqButton
            variant="ghost"
            size="sm"
            leadingIcon={Icons.Eye ?? Icons.Search}
            onClick={() => setRevealOpen(true)}
            disabled={tombstoned}
          >
            Reveal email
          </HqButton>
        </div>
      </HqCard>

      <HqCard>
        <div
          className="hq-mono"
          style={{
            fontSize: 10.5,
            color: "var(--hq-text-dim)",
            letterSpacing: ".1em",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          Lawful basis
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {LAWFUL_BASES.map((b) => (
            <BasisRow
              key={b}
              value={b}
              active={detail.lawfulBasis === b}
              onClick={() => !tombstoned && !busy && void changeBasis(b)}
              disabled={tombstoned || busy}
            />
          ))}
        </div>
      </HqCard>

      <HqCard>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Icons.Inbox
            width={16}
            height={16}
            sw={1.7}
            style={{ color: detail.feedbackEnabled ? "var(--hq-accent)" : "var(--hq-text-dim)" }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 500 }}>Feedback widget</div>
            <div style={{ fontSize: 11, color: "var(--hq-text-dim)", lineHeight: 1.45 }}>
              {detail.feedbackEnabled
                ? "Customer can open new tickets and reply to existing threads."
                : "Submissions return 403; existing threads stay readable."}
            </div>
          </div>
          <HqButton
            variant={detail.feedbackEnabled ? "secondary" : "primary"}
            size="sm"
            disabled={busy || tombstoned}
            onClick={() => void toggleFeedback()}
          >
            {busy ? "…" : detail.feedbackEnabled ? "Disable" : "Enable"}
          </HqButton>
        </div>
      </HqCard>

      <div style={{ display: "flex", gap: 8 }}>
        <HqButton
          variant="secondary"
          size="sm"
          leadingIcon={Icons.Download}
          onClick={() => void downloadExport()}
          disabled={tombstoned}
        >
          Export all data (Art. 15)
        </HqButton>
      </div>

      <RevealEmailModal
        open={revealOpen}
        customerId={detail.id}
        onClose={() => setRevealOpen(false)}
      />
      <RecordConsentModal
        open={consentOpen}
        customerId={detail.id}
        onClose={() => setConsentOpen(false)}
        onRecorded={() => {
          setConsentOpen(false);
          onRefresh();
        }}
      />
    </div>
  );
}

function ConsentChip({ purpose, granted }: { purpose: ConsentPurpose; granted: boolean }) {
  return (
    <div
      style={{
        padding: 10,
        background: "var(--hq-bg-input)",
        border: `1px solid ${granted ? "rgba(52,211,153,.25)" : "var(--hq-border-input)"}`,
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <HqStatusDot tone={granted ? "success" : "neutral"} size={8} />
      <span style={{ fontSize: 12, color: "var(--hq-text)", flex: 1 }}>
        {PURPOSE_LABEL[purpose] ?? purpose}
      </span>
      <HqBadge tone={granted ? "success" : "neutral"} size="sm">
        {granted ? "opt-in" : "off"}
      </HqBadge>
    </div>
  );
}

function BasisRow({
  value,
  active,
  onClick,
  disabled,
}: {
  value: LawfulBasis;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        background: active ? "var(--hq-bg-active)" : "transparent",
        border: "1px solid transparent",
        borderRadius: "var(--hq-radius-md)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        color: "var(--hq-text)",
        textAlign: "left",
        fontSize: 11.5,
      }}
    >
      <HqRadio checked={active} onChange={() => onClick()} ariaLabel={BASIS_LABEL[value]} />
      {BASIS_LABEL[value]}
    </button>
  );
}

// ─── Billing tab (placeholder) ────────────────────────────────────────────

function BillingTab() {
  return (
    <HqCard>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 8 }}>
        <Icons.Tag width={20} height={20} sw={1.6} style={{ color: "var(--hq-text-dim)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            Billing — coming soon
          </div>
          <div style={{ fontSize: 12, color: "var(--hq-text-dim)", lineHeight: 1.5 }}>
            Invoice history, subscription state, and payment-method management land here once Stripe
            (or equivalent) is wired up. Until then license tier is set at issue-time.
          </div>
        </div>
      </div>
    </HqCard>
  );
}

// ─── Danger Zone tab ──────────────────────────────────────────────────────

function DangerTab({
  detail,
  onRefresh,
  toast,
}: {
  detail: CustomerDetail;
  onRefresh: () => void;
  toast: ToastFn;
}) {
  const [eraseOpen, setEraseOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const tombstoned = detail.tombstonedAt != null;
  const anyActive = detail.linkedLicenses.some((l) => !l.revoked);

  async function suspend() {
    const reason = prompt("Suspension reason (lands in audit + revoked_reason)", "non-payment");
    if (!reason) return;
    setBusy("suspend");
    const res = await CustomersApi.suspend(detail.id, reason.trim());
    setBusy(null);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    toast(`Suspended ${res.data.suspended} license(s).`, "warn");
    onRefresh();
  }

  async function unsuspend() {
    setBusy("unsuspend");
    const res = await CustomersApi.unsuspend(detail.id);
    setBusy(null);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    toast(`Restored ${res.data.unsuspended} license(s).`, "success");
    onRefresh();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <HqCard>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Suspend all licenses</div>
        <div
          style={{ fontSize: 12, color: "var(--hq-text-dim)", lineHeight: 1.5, marginBottom: 12 }}
        >
          Revokes every active license tied to this customer with a matching prefix in the
          revoke-reason so Unsuspend can restore just these (not manual revokes).
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <HqButton
            variant="danger"
            size="sm"
            leadingIcon={Icons.Revoke}
            disabled={tombstoned || !anyActive || busy === "suspend"}
            loading={busy === "suspend"}
            onClick={() => void suspend()}
          >
            Suspend all
          </HqButton>
          <HqButton
            variant="secondary"
            size="sm"
            disabled={tombstoned || busy === "unsuspend"}
            loading={busy === "unsuspend"}
            onClick={() => void unsuspend()}
          >
            Restore suspended
          </HqButton>
        </div>
      </HqCard>

      <HqCard>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          Erase customer (Art. 17)
        </div>
        <div
          style={{ fontSize: 12, color: "var(--hq-text-dim)", lineHeight: 1.5, marginBottom: 12 }}
        >
          Tombstones the customer row: <code>email</code> → <code>[erased]</code>,{" "}
          <code>display_name</code> + <code>notes</code> nulled, audit kept (legal hold). Licenses
          themselves stay around for fraud history but can no longer be re-linked to PII.
        </div>
        <HqButton
          variant="danger"
          size="sm"
          leadingIcon={Icons.Revoke}
          onClick={() => setEraseOpen(true)}
          disabled={tombstoned}
        >
          Erase customer
        </HqButton>
      </HqCard>

      <EraseCustomerModal
        open={eraseOpen}
        customer={detail}
        onClose={() => setEraseOpen(false)}
        onErased={() => {
          setEraseOpen(false);
          onRefresh();
        }}
      />
    </div>
  );
}

// ─── Modals (reveal email, erase, record consent) ─────────────────────────

function RevealEmailModal({
  open,
  customerId,
  onClose,
}: {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
}) {
  const { toast } = useShell();
  const [reason, setReason] = useState("");
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setReason("");
      setRevealed(null);
      setBusy(false);
      setErr(null);
    }
  }, [open]);

  async function submit() {
    if (!customerId) return;
    if (reason.trim().length < 4) {
      setErr("Reason required (min 4 chars) — lands in audit log.");
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await CustomersApi.revealEmail(customerId, reason.trim());
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    setRevealed(res.data.email);
    toast("Email revealed + audited.", "warn");
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icons.Eye width={14} height={14} sw={1.7} style={{ color: "var(--hq-warn)" }} />
          Reveal email
        </span>
      }
      width={440}
      footer={
        revealed ? (
          <HqButton variant="primary" onClick={onClose}>
            Done
          </HqButton>
        ) : (
          <>
            <HqButton variant="ghost" onClick={onClose}>
              Cancel
            </HqButton>
            <HqButton variant="primary" loading={busy} onClick={() => void submit()}>
              Reveal &amp; audit
            </HqButton>
          </>
        )
      }
    >
      {revealed ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--hq-text-muted)", lineHeight: 1.55 }}>
            Audited as <code>customer.reveal_email</code>. Close this modal as soon as you've used
            the address.
          </p>
          <div
            style={{
              padding: "10px 12px",
              background: "var(--hq-bg-input)",
              border: "1px solid var(--hq-border-input)",
              borderRadius: "var(--hq-radius-md)",
              fontFamily: "var(--hq-font-mono)",
              fontSize: 13,
              wordBreak: "break-all",
            }}
          >
            {revealed}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--hq-text-muted)", lineHeight: 1.55 }}>
            The raw email is returned only via this route, and the audit row lands{" "}
            <strong>before</strong> the body. Explain why you need it — the reason is part of the
            audit trail.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. responding to support ticket #42; DSR Art. 15; chargeback inquiry…"
            style={{
              width: "100%",
              background: "var(--hq-bg-input)",
              color: "var(--hq-text)",
              border: "1px solid var(--hq-border-input)",
              borderRadius: "var(--hq-radius-lg)",
              padding: "10px 12px",
              fontSize: 12.5,
              fontFamily: "var(--hq-font-sans)",
              resize: "vertical",
              outline: "none",
            }}
          />
          {err && (
            <div
              style={{
                padding: "8px 12px",
                borderRadius: "var(--hq-radius-md)",
                background: "var(--hq-danger-soft)",
                color: "var(--hq-danger)",
                fontSize: 12,
                border: "1px solid rgba(251,113,133,.25)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <HqIconButton icon={Icons.X} size="sm" title="Dismiss" onClick={() => setErr(null)} />
              {err}
            </div>
          )}
        </div>
      )}
    </HqModal>
  );
}

function EraseCustomerModal({
  open,
  customer,
  onClose,
  onErased,
}: {
  open: boolean;
  customer: CustomerDetail | null;
  onClose: () => void;
  onErased: () => void;
}) {
  const { toast } = useShell();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const confirmTarget = customer?.emailMasked ?? "";

  useEffect(() => {
    if (!open) {
      setTyped("");
      setBusy(false);
      setErr(null);
    }
  }, [open]);

  async function submit() {
    if (!customer) return;
    if (typed !== confirmTarget) {
      setErr(`Type ${confirmTarget} to confirm.`);
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await CustomersApi.erase(customer.id);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onErased();
    toast(res.data.alreadyErased ? "Already tombstoned." : "Customer erased (Art. 17).", "warn");
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icons.Revoke width={14} height={14} sw={1.7} style={{ color: "var(--hq-danger)" }} />
          Erase customer (Art. 17)
        </span>
      }
      width={440}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="danger"
            leadingIcon={Icons.Revoke}
            loading={busy}
            onClick={() => void submit()}
            disabled={typed !== confirmTarget}
          >
            Erase now
          </HqButton>
        </>
      }
    >
      <p
        style={{
          margin: "0 0 10px",
          fontSize: 12.5,
          color: "var(--hq-text-muted)",
          lineHeight: 1.55,
        }}
      >
        Sets <code>tombstoned_at</code>, replaces <code>email</code> with the sentinel{" "}
        <code>[erased]</code>, nulls <code>display_name</code> and <code>notes</code>. Non-PII
        license facts (revocation, anti-fraud lawful-basis) are kept. Audit rows are{" "}
        <strong>not</strong> deleted (legal-hold). This is irreversible.
      </p>
      <HqInput
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={`Type ${confirmTarget} to confirm`}
        mono
        hint={`Confirm by typing the masked email: ${confirmTarget}`}
      />
      {err && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
          }}
        >
          {err}
        </div>
      )}
    </HqModal>
  );
}

function RecordConsentModal({
  open,
  customerId,
  onClose,
  onRecorded,
}: {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [purpose, setPurpose] = useState<ConsentPurpose>("telemetry");
  const [granted, setGranted] = useState(true);
  const [source, setSource] = useState<ConsentSource>("admin");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPurpose("telemetry");
      setGranted(true);
      setSource("admin");
      setEvidence("");
      setBusy(false);
      setErr(null);
    }
  }, [open]);

  async function submit() {
    if (!customerId) return;
    setBusy(true);
    setErr(null);
    const res = await CustomersApi.recordConsent(
      customerId,
      purpose,
      granted,
      source,
      evidence.trim() || undefined,
    );
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onRecorded();
  }

  const purposeOpts = useMemo(
    () => CONSENT_PURPOSES.map((p) => ({ value: p, label: PURPOSE_LABEL[p] ?? p })),
    [],
  );
  const sourceOpts = useMemo(() => CONSENT_SOURCES.map((s) => ({ value: s, label: s })), []);

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title="Record consent"
      width={460}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="primary"
            leadingIcon={Icons.Check}
            loading={busy}
            onClick={() => void submit()}
          >
            Append to ledger
          </HqButton>
        </>
      }
    >
      <p
        style={{
          margin: "0 0 12px",
          fontSize: 12.5,
          color: "var(--hq-text-muted)",
          lineHeight: 1.55,
        }}
      >
        Append-only — current state is the latest entry per purpose.
      </p>
      <Field label="Purpose">
        <Segmented
          options={purposeOpts}
          value={purpose}
          onChange={(v) => setPurpose(v as ConsentPurpose)}
        />
      </Field>
      <Field label="Granted?">
        <Segmented
          options={[
            { value: "yes", label: "Opt-in" },
            { value: "no", label: "Opt-out" },
          ]}
          value={granted ? "yes" : "no"}
          onChange={(v) => setGranted(v === "yes")}
        />
      </Field>
      <Field label="Source">
        <Segmented
          options={sourceOpts}
          value={source}
          onChange={(v) => setSource(v as ConsentSource)}
        />
      </Field>
      <Field label="Evidence (optional)" hint="URL, message-id, ticket #…">
        <HqInput value={evidence} onChange={(e) => setEvidence(e.target.value)} mono />
      </Field>
      {err && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
          }}
        >
          {err}
        </div>
      )}
    </HqModal>
  );
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 3,
        gap: 2,
        background: "var(--hq-bg-input)",
        borderRadius: "var(--hq-radius-lg)",
        border: "1px solid var(--hq-border-input)",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: "0 10px",
              height: 26,
              borderRadius: "var(--hq-radius-md)",
              background: active ? "var(--hq-bg-active)" : "transparent",
              color: active ? "var(--hq-text)" : "var(--hq-text-muted)",
              fontSize: 11.5,
              border: "none",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)", fontWeight: 500 }}>
          {label}
        </span>
        {hint && <span style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function fmtDate(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtDateTime(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
